import type { RouteViewModelControlTask } from '../../types/routeViewModel';
import { isSequentialStopsReadModeEnabled } from '../sequentialStops/sequentialStopsReadFlags';
import {
  logSequentialStopsReadShadow,
  sortStopsByPosition
} from '../sequentialStops/sequentialStopsOrdering';
import { mergeStopOperationalMetadata } from '../routes/execution/operationalContextFacade';
import type {
  EnrichedExecutionStop,
  ExecutionCargoHydration,
  ExecutionEdgeHydration,
  ExecutionProgressHydration,
  ExecutionStopHydration,
  TimelineStage
} from './types';

const LOADING_DOC_TASKS = new Set(['LOADING_PHOTO', 'LOADING_DOCS', 'LOADING_DOCUMENTS']);
const UNLOADING_DOC_TASKS = new Set(['UNLOADING_PHOTO', 'UNLOADING_DOCS', 'UNLOADING_DOCUMENTS']);

export function isLoadingStop(type: string): boolean {
  return String(type).toLowerCase() === 'loading';
}

export function stopShortLabel(
  stop: ExecutionStopHydration,
  index: number,
  itineraryIndex?: number
): string {
  const kind = isLoadingStop(stop.type) ? 'Погрузка' : 'Выгрузка';
  const seq =
    stop.position != null && Number.isFinite(Number(stop.position))
      ? Number(stop.position) + 1
      : (itineraryIndex ?? index) + 1;
  return `${kind} #${seq}`;
}

export function cargoAtStop(
  cargo: ExecutionCargoHydration[],
  stopUid: string,
  type: string
): ExecutionCargoHydration[] {
  if (isLoadingStop(type)) {
    return cargo.filter((c) => c.fromExecutionStopUid === stopUid);
  }
  return cargo.filter((c) => c.toExecutionStopUid === stopUid);
}

export function edgesToUnloadStop(
  edges: ExecutionEdgeHydration[],
  unloadUid: string
): ExecutionEdgeHydration[] {
  return edges.filter((e) => e.toExecutionStopUid === unloadUid);
}

export function enrichStops(
  hydration: ExecutionProgressHydration,
  tasks: RouteViewModelControlTask[] = []
): EnrichedExecutionStop[] {
  const openTasks = tasks.filter((t) => String(t.status).toUpperCase() !== 'COMPLETED');

  return hydration.stops.map((stop, index) => {
    const relatedCargo = cargoAtStop(hydration.cargo, stop.executionStopUid, stop.type);
    const cargoPlanned = relatedCargo.reduce((s, c) => s + c.plannedQty, 0);
    const cargoLoaded = relatedCargo.reduce((s, c) => s + c.loadedQty, 0);
    const cargoUnloaded = relatedCargo.reduce((s, c) => s + c.unloadedQty, 0);

    const isLoading = isLoadingStop(stop.type);
    const pendingWorkflowTasks = openTasks.filter((t) => {
      const tt = String(t.taskType).toUpperCase();
      if (isLoading) return LOADING_DOC_TASKS.has(tt);
      return UNLOADING_DOC_TASKS.has(tt);
    });

    const blockers: string[] = [];
    if (!stop.isCompleted && stop.completionPercent > 0 && stop.completionPercent < 100) {
      blockers.push('partial_execution');
    }
    if (relatedCargo.some((c) => c.isPartiallyDelivered || c.executionState.includes('PARTIAL'))) {
      blockers.push('partial_cargo');
    }

    return {
      ...stop,
      label: stopShortLabel(stop, index),
      cargoLoaded,
      cargoUnloaded,
      cargoPlanned,
      pendingWorkflowTasks,
      blockers
    };
  });
}

function buildExecutionTimelineLegacy(hydration: ExecutionProgressHydration): TimelineStage[] {
  const stages: TimelineStage[] = [];
  const loadingStops = hydration.stops.filter((s) => isLoadingStop(s.type));
  const unloadingStops = hydration.stops.filter((s) => !isLoadingStop(s.type));

  for (const stop of loadingStops) {
    stages.push({
      key: `load-${stop.executionStopUid}`,
      kind: 'loading',
      stopUid: stop.executionStopUid,
      label: stopShortLabel(stop, loadingStops.indexOf(stop)),
      executionState: stop.executionState,
      completionPercent: stop.completionPercent,
      blockers: stop.isCompleted ? [] : ['loading_pending'],
      isPartial: stop.completionPercent > 0 && stop.completionPercent < 100,
      timestamps: [
        { label: 'Прибытие', at: stop.arrivedAt },
        { label: 'Начало погрузки', at: stop.loadingStartedAt },
        { label: 'Завершение погрузки', at: stop.loadingCompletedAt }
      ]
    });
  }

  const transitPercent =
    hydration.progress.allLoadingComplete && !hydration.progress.allUnloadingComplete
      ? hydration.progress.cargoPercent
      : hydration.progress.allLoadingComplete
        ? 100
        : 0;

  stages.push({
    key: 'transit',
    kind: 'transit',
    label: 'В пути',
    executionState: hydration.progress.allLoadingComplete ? 'IN_TRANSIT' : 'PENDING_TRANSIT',
    completionPercent: transitPercent,
    blockers: hydration.progress.allLoadingComplete ? [] : ['loading_incomplete'],
    isPartial: hydration.progress.blockers.includes('partial_delivery'),
    timestamps: []
  });

  for (const stop of unloadingStops) {
    stages.push({
      key: `unload-${stop.executionStopUid}`,
      kind: 'unloading',
      stopUid: stop.executionStopUid,
      label: stopShortLabel(stop, unloadingStops.indexOf(stop)),
      executionState: stop.executionState,
      completionPercent: stop.completionPercent,
      blockers: stop.isCompleted ? [] : ['unloading_pending'],
      isPartial: stop.completionPercent > 0 && stop.completionPercent < 100,
      timestamps: [
        { label: 'Прибытие', at: stop.arrivedAt },
        { label: 'Начало выгрузки', at: stop.unloadingStartedAt },
        { label: 'Завершение выгрузки', at: stop.unloadingCompletedAt }
      ]
    });
  }

  return stages;
}

function buildExecutionTimelineSequential(hydration: ExecutionProgressHydration): TimelineStage[] {
  const ordered = sortStopsByPosition(hydration.stops);
  logSequentialStopsReadShadow({
    routeId: hydration.routeId,
    context: 'buildExecutionTimeline',
    stops: hydration.stops
  });

  return ordered.map((stop, idx) => {
    const loading = isLoadingStop(stop.type);
    return {
      key: `${loading ? 'load' : 'unload'}-${stop.executionStopUid}`,
      kind: loading ? ('loading' as const) : ('unloading' as const),
      stopUid: stop.executionStopUid,
      label: stopShortLabel(stop, idx, idx),
      executionState: stop.executionState,
      completionPercent: stop.completionPercent,
      blockers: stop.isCompleted
        ? []
        : [loading ? 'loading_pending' : 'unloading_pending'],
      isPartial: stop.completionPercent > 0 && stop.completionPercent < 100,
      timestamps: loading
        ? [
            { label: 'Прибытие', at: stop.arrivedAt },
            { label: 'Начало погрузки', at: stop.loadingStartedAt },
            { label: 'Завершение погрузки', at: stop.loadingCompletedAt }
          ]
        : [
            { label: 'Прибытие', at: stop.arrivedAt },
            { label: 'Начало выгрузки', at: stop.unloadingStartedAt },
            { label: 'Завершение выгрузки', at: stop.unloadingCompletedAt }
          ]
    };
  });
}

export function buildExecutionTimeline(hydration: ExecutionProgressHydration): TimelineStage[] {
  if (isSequentialStopsReadModeEnabled()) {
    return buildExecutionTimelineSequential(hydration);
  }
  return buildExecutionTimelineLegacy(hydration);
}

export function availableActionsForStop(stop: ExecutionStopHydration): string[] {
  const isLoading = isLoadingStop(stop.type);
  const state = stop.executionState;
  if (isLoading) {
    if (state === 'PENDING_LOADING') return ['arriveForLoading'];
    if (state === 'ARRIVED_FOR_LOADING') return ['startLoading'];
    if (state === 'LOADING' || state === 'PARTIALLY_EXECUTED') return ['completeLoading'];
    return [];
  }
  if (state === 'PENDING_UNLOADING') return ['arriveForUnloading'];
  if (state === 'ARRIVED_FOR_UNLOADING') return ['startUnloading'];
  if (state === 'UNLOADING' || state === 'PARTIALLY_EXECUTED') return ['completeUnloading'];
  return [];
}

export function actionButtonLabel(action: string): string {
  const labels: Record<string, string> = {
    arriveForLoading: 'Прибыл на погрузку',
    startLoading: 'Начать погрузку',
    completeLoading: 'Завершить погрузку',
    arriveForUnloading: 'Прибыл на выгрузку',
    startUnloading: 'Начать выгрузку',
    completeUnloading: 'Завершить выгрузку'
  };
  return labels[action] ?? action;
}

/** Merge structure stop operational metadata when hydration lacks them. */
export function mergeStopAddresses(
  hydration: ExecutionProgressHydration,
  structureStops: Array<Record<string, unknown>>
): ExecutionProgressHydration {
  return mergeStopOperationalMetadata(hydration, structureStops);
}
